import React, { useEffect } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { successMessages } from '@/constants/feedbackMessages'
import { Button, ModalBase, NumberInput } from '@components/atoms'
import { CookieStorage } from '@helpers'
import { yupResolver } from '@hookform/resolvers/yup'
import { editPhonechema } from '@schemas/settings/index.schema'
import { dashboardAPI } from '@services/DashboardAPI'
import { trackingAPI } from '@services/TrackingAPI'
import { modalCreators } from '@store/ducks/modal'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { css } from '@styles/theme'
import moment from 'moment'

type Inputs = {
	name: string
	birthdate: string
	whatsapp: string
	phone: string
	email: string
	cpfCnpj: string
}

const buttonPostpone = css({
	fontSize: 12,
	lineHeight: 1.5,
	cursor: 'pointer',
	textDecoration: 'none',
	'&:hover': {
		textDecoration: 'underline',
	},
})

export const ModalEditPhone = () => {
	const dispatch = useAppDispatch()
	const [editInfo, { isLoading, isSuccess }] =
		dashboardAPI.useEditInfoMutation()

	const {
		modal: { modalName, modalInfo },
	} = useAppSelector((state) => state)

	const cookiesStorage = new CookieStorage()

	const {
		register,
		reset,
		setValue,
		handleSubmit,
		formState: { errors },
	} = useForm<Inputs>({
		mode: 'onBlur',
		resolver: yupResolver(editPhonechema),
	})

	const [setTracking] = trackingAPI.useSetTrackingMutation()

	const onSubmit: SubmitHandler<Inputs> = (data) => {
		editInfo({
			name: data.name,
			birthdate: moment(data?.birthdate).format('YYYY-MM-DD'),
			whatsapp: data.whatsapp || '',
			phone: data.phone || '',
			cep: modalInfo?.address?.cep || '',
			street: modalInfo?.address?.street || '',
			number: modalInfo?.address?.number || '',
			district: modalInfo?.address?.district || '',
			city: modalInfo?.address?.city || '',
			uf: modalInfo?.address?.uf || '',
		})
		if (modalInfo?.id) {
			setTracking({
				clientId: modalInfo?.id,
				action: 'phoneModalUpdate',
			})
		}
		dispatch(modalCreators.closeModal())
		reset()
	}

	const remiderPhoneModal = () => {
		if (modalInfo?.id) {
			setTracking({
				clientId: modalInfo.id,
				action: 'phoneModalPostpone',
			})
		}
		cookiesStorage.set({
			key: 'phoneModalPostpone',
			value: 'phoneModalPostpone',
			daysToExpire: 5,
		})
		dispatch(notificationCreators.show(successMessages.remiderUpdatePhone))
		dispatch(modalCreators.closeModal())
	}

	useEffect(() => {
		if (modalInfo?.id) {
			setTracking({
				clientId: modalInfo.id,
				action: 'phoneModalView',
			})
		}
	}, [modalInfo?.id, setTracking])

	useEffect(() => {
		if (modalInfo) {
			setValue('name', modalInfo?.name)
			setValue('cpfCnpj', modalInfo?.cpfCnpj || '')
			setValue('email', modalInfo?.email)
			setValue('phone', modalInfo?.phone || '')
			setValue('whatsapp', modalInfo?.whatsapp || '')
			setValue(
				'birthdate',
				moment.utc(modalInfo?.birthdate).format('YYYY-MM-DD')
			)
		}
	}, [modalInfo, setValue])

	useEffect(() => {
		if (isSuccess) {
			dispatch(notificationCreators.show(successMessages.updatedPhone))
		}
	}, [dispatch, isSuccess])

	return (
		<ModalBase
			title='Não perca nenhuma promoção!'
			open={modalName === 'editPhone'}
			onCloseFunction={() => {
				if (modalInfo?.id) {
					cookiesStorage.set({
						key: 'closePhoneModal',
						value: 'closePhoneModal',
						daysToExpire: 0.5,
					})
					setTracking({
						clientId: modalInfo?.id,
						action: 'phoneModalClose',
					})
				}
			}}
		>
			<>
				<p
					style={{
						fontSize: 16,
						lineHeight: 1.5,
						marginTop: '34px',
					}}
				>
					Atualize seu fone e seja avisado sobre descontos e novos materiais
				</p>
				<form
					onSubmit={handleSubmit(onSubmit)}
					style={{ width: '100%', marginTop: '32px' }}
				>
					<NumberInput
						placeholder='Telefone'
						error={errors.phone?.message}
						{...register('phone')}
						css={{ marginBottom: '14px', color: '$neutral120' }}
						onValueChange={(v) => setValue('phone', v.value)}
						defaultValue={modalInfo?.phone || ''}
						format='(##) #####-####'
					/>
					{errors.phone?.message && (
						<div style={{ position: 'relative' }}>
							<p
								style={{ color: '#FF4D4F', fontSize: 12, position: 'absolute' }}
							>
								{errors.phone?.message}
							</p>
						</div>
					)}
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: '20px',
							marginTop: '44px',
						}}
					>
						<Button loading={isLoading}>Atualizar</Button>
						<div>
							<button
								type='button'
								onClick={() => remiderPhoneModal()}
								className={buttonPostpone()}
							>
								Lembrar mais tarde
							</button>
						</div>
					</div>
				</form>
			</>
		</ModalBase>
	)
}
